import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../services/apiClient';
import UploadDropzone from '../components/UploadDropzone';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

const PLACEMENT = 'destinations';
const SECTION_KEY = 'destinations';
const BANNER_TYPE = 'top_slider';

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  cta_link: '',
  image_url: '',
  status: 'published',
  sort_order: 0,
};

const sortByOrder = (a, b) => {
  const diff = Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
  if (diff !== 0) return diff;
  return String(b.created_at || '').localeCompare(String(a.created_at || ''));
};

export default function ContentPopularDestinationsPage({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const sortedItems = useMemo(() => [...items].sort(sortByOrder), [items]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet(`/api/admin/content/banners?placement=${PLACEMENT}&limit=200`, token);
      const list = res?.data || res?.items || res || [];
      const filtered = Array.isArray(list)
        ? list.filter((row) => String(row.placement || '').toLowerCase() === PLACEMENT)
        : [];
      setItems(filtered);
    } catch (err) {
      setItems([]);
      setError(err.message || 'Failed to load destinations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const onEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title || '',
      subtitle: row.subtitle || '',
      cta_link: row.cta_link || '',
      image_url: row.image_url || '',
      status: row.status || 'draft',
      sort_order: Number(row.sort_order ?? 0),
    });
  };

  const onSave = async () => {
    setError('');
    if (!String(form.title || '').trim()) {
      setError('Destination name is required.');
      return;
    }
    if (!String(form.image_url || '').trim()) {
      setError('Destination image is required.');
      return;
    }

    const payload = {
      placement: PLACEMENT,
      section_key: SECTION_KEY,
      banner_type: BANNER_TYPE,
      title: String(form.title || '').trim(),
      subtitle: String(form.subtitle || '').trim(),
      cta_link: String(form.cta_link || '').trim(),
      image_url: String(form.image_url || '').trim(),
      status: form.status || 'draft',
      sort_order: Number(form.sort_order ?? 0),
    };

    setSaving(true);
    try {
      if (editing?.id) {
        await apiPut(`/api/admin/content/banners/${editing.id}`, payload, token);
      } else {
        await apiPost('/api/admin/content/banners', payload, token);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Unable to save destination.');
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (row) => {
    setError('');
    try {
      await apiPatch(`/api/admin/content/banners/${row.id}/toggle`, {}, token);
      await load();
    } catch (err) {
      setError(err.message || 'Unable to update status.');
    }
  };

  const onDelete = async (row) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Delete this destination?');
    if (!ok) return;
    setError('');
    try {
      await apiDelete(`/api/admin/content/banners/${row.id}`, token);
      if (editing?.id === row.id) resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Unable to delete destination.');
    }
  };

  const moveInList = async (row, direction) => {
    const idx = sortedItems.findIndex((item) => item.id === row.id);
    if (idx < 0) return;
    const swapWith = sortedItems[idx + direction];
    if (!swapWith) return;
    setError('');
    try {
      await apiPatch(
        '/api/admin/content/banners/reorder',
        {
          items: [
            { id: row.id, sort_order: Number(swapWith.sort_order ?? 0) },
            { id: swapWith.id, sort_order: Number(row.sort_order ?? 0) },
          ],
        },
        token
      );
      await load();
    } catch (err) {
      setError(err.message || 'Unable to reorder.');
    }
  };

  const previewUrl = useMemo(() => resolveAssetUrl(form.image_url), [form.image_url]);

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h4 className="mb-0">Popular Destinations</h4>
          <small className="text-muted">
            Manage the coverflow slides shown on the landing page. If none are published, the homepage falls back to package destinations.
          </small>
        </div>
        <Button variant="outline-secondary" onClick={load} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Refresh
            </>
          ) : (
            'Refresh'
          )}
        </Button>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3">
        <Col lg={5}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="fw-semibold">{editing ? 'Edit destination slide' : 'Add destination slide'}</div>
                  <div className="text-muted small">Recommended image ratio: 3:4.8 (e.g. 900x1440).</div>
                </div>
                {editing ? (
                  <Button size="sm" variant="outline-secondary" onClick={resetForm}>
                    Cancel
                  </Button>
                ) : null}
              </div>

              <Form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                <Row className="g-2">
                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Destination name</Form.Label>
                    <Form.Control
                      value={form.title}
                      onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Rajasthan"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Subtitle (optional)</Form.Label>
                    <Form.Control
                      value={form.subtitle}
                      onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                      placeholder="e.g. Tours (02)"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Link (optional)</Form.Label>
                    <Form.Control
                      value={form.cta_link}
                      onChange={(e) => setForm((p) => ({ ...p, cta_link: e.target.value }))}
                      placeholder="/packages/list?destination=rajasthan"
                    />
                  </Col>

                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Destination image</Form.Label>
                    {previewUrl ? (
                      <div className="mb-2 rounded border" style={{ height: 160, overflow: 'hidden', background: '#f1f5f9' }}>
                        <img
                          src={previewUrl}
                          alt="Preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                        />
                      </div>
                    ) : null}
                    <UploadDropzone
                      token={token}
                      uploadPath="/api/admin/content/banners/upload"
                      label="Upload destination image"
                      onUploaded={(path) => setForm((p) => ({ ...p, image_url: path }))}
                      onUploadedMany={(paths) => setForm((p) => ({ ...p, image_url: paths?.[0] || p.image_url }))}
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Status</Form.Label>
                    <Form.Select
                      value={form.status}
                      onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                    >
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Sort order</Form.Label>
                    <Form.Control
                      type="number"
                      value={Number(form.sort_order ?? 0)}
                      onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))}
                    />
                  </Col>

                  <Col md={12} className="d-flex gap-2 pt-2">
                    <Button variant="primary" onClick={onSave} disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Saving
                        </>
                      ) : (
                        editing ? 'Update' : 'Create'
                      )}
                    </Button>
                    <Button variant="outline-secondary" onClick={resetForm} disabled={saving}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div className="fw-semibold">Slides</div>
                  <div className="text-muted small">{sortedItems.length} item(s)</div>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <Table size="sm" bordered hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 96 }}>Preview</th>
                      <th>Title</th>
                      <th>Subtitle</th>
                      <th style={{ width: 110 }}>Status</th>
                      <th style={{ width: 70 }}>Sort</th>
                      <th style={{ width: 240 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((row, idx) => (
                      <tr key={row.id}>
                        <td>
                          {row.image_url ? (
                            <img
                              src={resolveAssetUrl(row.image_url)}
                              alt={row.title || 'Destination'}
                              style={{ width: 76, height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }}
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-muted small">No image</span>
                          )}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 200 }}>
                          {row.title || <span className="text-muted">(Untitled)</span>}
                          {row.cta_link ? (
                            <div className="text-muted small mt-1 text-truncate" style={{ maxWidth: 200 }}>
                              Link: {row.cta_link}
                            </div>
                          ) : null}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>
                          {row.subtitle || <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {row.status === 'published' ? <Badge bg="success">Published</Badge> : <Badge bg="secondary">Draft</Badge>}
                        </td>
                        <td>{Number(row.sort_order ?? 0)}</td>
                        <td className="text-nowrap">
                          <Button size="sm" variant="outline-primary" onClick={() => onEdit(row)}>
                            Edit
                          </Button>{' '}
                          <Button size="sm" variant="outline-secondary" onClick={() => onToggle(row)}>
                            Toggle
                          </Button>{' '}
                          <Button size="sm" variant="outline-secondary" onClick={() => moveInList(row, -1)} disabled={idx === 0}>
                            Up
                          </Button>{' '}
                          <Button size="sm" variant="outline-secondary" onClick={() => moveInList(row, 1)} disabled={idx === sortedItems.length - 1}>
                            Down
                          </Button>{' '}
                          <Button size="sm" variant="outline-danger" onClick={() => onDelete(row)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {!loading && sortedItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No destination slides yet.
                        </td>
                      </tr>
                    ) : null}
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          <Spinner size="sm" className="me-2" /> Loading...
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
